import React, { useState, useMemo } from 'react';

interface Overlay {
  id: string;
  type: string;
  content: string;
  startTime: number;
  endTime: number;
  x: number;
  y: number;
  scale: number;
}

interface EmojiPickerProps {
  currentTime: number;
  duration: number;
  onAdd: (overlay: Overlay) => void;
  onClose: () => void;
  selectedOverlay?: Overlay | null;
  onUpdate?: (overlay: Overlay) => void;
  onDelete?: (id: string) => void;
}

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  { label: '🔥 Popular', emojis: ['🔥', '❤️', '💯', '⭐', '🎉', '👏', '👍', '💪', '🚀', '💡', '✅', '📈', '💰', '🎯', '😍', '🤯'] },
  { label: '😀 Smileys', emojis: ['😀', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎', '🤩', '😱', '😡', '🥺', '😢', '🤔', '🙄', '😴'] },
  { label: '👋 Gestures', emojis: ['👋', '👍', '👎', '👏', '🙌', '🤝', '✌️', '🤟', '🤙', '💪', '🙏', '☝️', '👆', '👇', '👈', '👉'] },
  { label: '❤️ Hearts', emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '💖', '💗', '💘', '💝', '🫶', '💓'] },
  { label: '🎵 Music', emojis: ['🎵', '🎶', '🎤', '🎧', '🎸', '🥁', '🎹', '🎺', '🎻', '🎼', '🔊', '🔇', '📢', '🔔', '🔕', '🎙️'] },
  { label: '⚡ Symbols', emojis: ['⚡', '💫', '✨', '🌟', '💥', '💢', '💤', '💬', '💭', '🏆', '👑', '💎', '🎁', '🎀', '🏅', '🥇'] },
  { label: '🍕 Food', emojis: ['🍕', '🍔', '🍟', '🌮', '🍣', '🍩', '🍦', '🍰', '☕', '🍺', '🥂', '🍷', '🍸', '🧃', '🥤', '🧋'] },
];

const EmojiPicker: React.FC<EmojiPickerProps> = ({
  currentTime, duration, onAdd, onClose, selectedOverlay, onUpdate, onDelete,
}) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(0);

  const filteredEmojis = useMemo(() => {
    if (!search) return EMOJI_CATEGORIES[activeCategory].emojis;
    // Simple filter: show all emojis from all categories
    return EMOJI_CATEGORIES.flatMap(c => c.emojis);
  }, [search, activeCategory]);

  const handleEmojiClick = (emoji: string) => {
    onAdd({
      id: `overlay-${Date.now()}`,
      type: 'emoji',
      content: emoji,
      startTime: currentTime,
      endTime: Math.min(currentTime + 3, duration),
      x: 0.5,
      y: 0.5,
      scale: 1.0,
    });
  };

  return (
    <div className="emoji-picker-panel">
      <div className="panel-header">
        <h3>Add Emoji</h3>
        <button className="btn-icon" onClick={onClose}>✕</button>
      </div>

      <div className="emoji-search">
        <input
          type="text"
          placeholder="🔍 Search emojis..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {!search && (
        <div className="emoji-categories">
          {EMOJI_CATEGORIES.map((cat, i) => (
            <button
              key={cat.label}
              className={`emoji-cat-btn ${i === activeCategory ? 'active' : ''}`}
              onClick={() => setActiveCategory(i)}
            >
              {cat.emojis[0]}
            </button>
          ))}
        </div>
      )}

      <div className="emoji-grid">
        {filteredEmojis.map((emoji, i) => (
          <button key={`${emoji}-${i}`} className="emoji-cell" onClick={() => handleEmojiClick(emoji)}>
            {emoji}
          </button>
        ))}
      </div>

      {/* Selected overlay controls */}
      {selectedOverlay && selectedOverlay.type === 'emoji' && onUpdate && onDelete && (
        <div className="emoji-overlay-controls">
          <div className="control-row">
            <label>Size</label>
            <input
              type="range"
              min="0.5"
              max="4"
              step="0.1"
              value={selectedOverlay.scale}
              onChange={(e) => onUpdate({ ...selectedOverlay, scale: parseFloat(e.target.value) })}
            />
            <span>{selectedOverlay.scale.toFixed(1)}x</span>
          </div>
          <div className="control-row">
            <label>Start</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max={duration}
              value={selectedOverlay.startTime.toFixed(1)}
              onChange={(e) => onUpdate({ ...selectedOverlay, startTime: parseFloat(e.target.value) })}
            />
          </div>
          <div className="control-row">
            <label>End</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max={duration}
              value={selectedOverlay.endTime.toFixed(1)}
              onChange={(e) => onUpdate({ ...selectedOverlay, endTime: parseFloat(e.target.value) })}
            />
          </div>
          <button className="btn-sm btn-danger" onClick={() => onDelete(selectedOverlay.id)}>Delete Emoji</button>
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
